import { useMutation, useQueryClient } from '@tanstack/react-query';

import { teamService } from '@/services/team-service';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { teamMutationKeys, teamQueryKeys } from './query-keys';

/**
 * Hook to remove a member from a team.
 * Accepts { userId: UUID } (the user to remove from the team).
 */
export function useRemoveTeamMember(tenantId?: UUID, teamId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: teamMutationKeys.removeTeamMember(tenantId, teamId),
    mutationFn: async (payload: { userId: UUID }) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!teamId) throw new Error('Missing team id');

      await teamService.revokeTeamMember(tenantId, teamId, payload.userId);

      await pollForResource(
        async () => {
          const member = await teamService.findTeamMember(
            tenantId,
            teamId,
            payload.userId
          );
          return member === null ? true : null;
        },
        { maxAttempts: 10, delayMs: 300 }
      );

      return payload.userId;
    },
    onSuccess: () => {
      // Invalidate team members query to refresh the list
      qc.invalidateQueries({
        queryKey: teamQueryKeys.memberList(tenantId, teamId),
        refetchType: 'all',
      });

      // Also invalidate teams query in case member counts are shown
      qc.invalidateQueries({
        queryKey: teamQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
